import React from 'react';
import {
	ImageMedia,
	RTEContext,
	RTEContextValue,
	RTEStyleType,
} from '../../libs/ideine-web-richtext/Context/RichtextConfig';
import { MediasUtils } from '../services/medias/MediasUtils';
import {
	useAuthenticationState,
	useI18nState,
} from '../../libs/IdeLink.Web.Common/src/Hooks/useAppState';
import { MediasApi } from '../services/medias/MediasApi';
import { EnvironmentHelper } from '../../libs/IdeLink.Web.Common/src/Helpers/EnvironmentHelper';

export const IdelinkRteConfig: RTEContextValue = {
	linkTargetLabel: 'editor.link.input.target.label',
	linkTargetPlaceholder: 'editor.link.input.target.placeholder',
	linkTitleLabel: 'editor.link.input.title.label',
	linkTitlePlaceholder: 'editor.link.input.title.placeholder',
	linkErrorMessageNotUrl: 'validators.isNotUrl',
	linkErrorMessageRequired: 'validators.isRequired',
	imageDropzoneTitle: 'editor.image.dropzone.title',
	imageDropzoneLabel: 'editor.image.dropzone.label',
	imageDropzoneCancelLabel: 'editor.image.dropzone.cancel.button.label',
	imageDropzoneLoadingMessage: 'editor.image.dropzone.loading.message',
	rteButtonAddLabel: 'editor.button.add.label',
	rteButtonCancelLabel: 'editor.button.cancel.label',
};

const Colors = {
	blueIdelink: '#3a62c7',
	blueHover: '#2749a1',
	deepBlueHover: '',
	alizarin_crimson: '#e22926',
	abbey: '#565a5e',
	purple: 'rebeccapurple',

	black: '#000000',
	black_44: '#444444',
	black_e4: '#e4e4e4',
	white: '#ffffff',
};

const RTEStyle: RTEStyleType = {
	popupButtonHoverColor: Colors.purple,

	buttonStandardTextColor: Colors.white,
	buttonStandardBackgroundColor: Colors.blueIdelink,
	buttonStandardBackgroundHoverColor: Colors.blueHover,

	buttonDangerBackgroundColor: Colors.white,
	buttonDangerTextColor: Colors.alizarin_crimson,
	buttonDangerBorderColor: Colors.alizarin_crimson,
	buttonDangerBackgroundHoverColor: Colors.alizarin_crimson + '25',

	inputTextTextColor: Colors.abbey,
	inputTextErrorColor: Colors.alizarin_crimson,

	inputTextStandardBorderColor: Colors.black_e4,
	inputTextStandardBorderColorFocus: Colors.black_44,

	videoListPageItemBackgroundColor: Colors.blueHover,
	videoListPageItemTextColor: Colors.white,
	videoListPageItemInProgressBackgroundColor: Colors.blueHover,
	videoListPageProgressBarThumb: Colors.blueHover,

	customButtonWidth: '155px',
	customButtonHeight: '40px',
};

export type RTEProviderProps = {
	value: RTEContextValue;
};

export const RTEProvider = ({ children, value }: React.PropsWithChildren<RTEProviderProps>) => {
	const i18n = useI18nState();
	const auth = useAuthenticationState();
	return (
		<RTEContext.Provider
			value={{
				linkTargetLabel: i18n.messages[value.linkTargetLabel || ''],
				linkTargetPlaceholder: i18n.messages[value.linkTargetPlaceholder || ''],
				linkTitleLabel: i18n.messages[value.linkTitleLabel || ''],
				linkTitlePlaceholder: i18n.messages[value.linkTitlePlaceholder || ''],
				linkErrorMessageNotUrl: i18n.messages[value.linkErrorMessageNotUrl || ''],
				linkErrorMessageRequired: i18n.messages[value.linkErrorMessageRequired || ''],
				imageDropzoneTitle: i18n.messages[value.imageDropzoneTitle || ''],
				imageDropzoneLabel: i18n.messages[value.imageDropzoneLabel || ''],
				imageDropzoneCancelLabel: i18n.messages[value.imageDropzoneCancelLabel || ''],
				imageDropzoneLoadingMessage: i18n.messages[value.imageDropzoneLoadingMessage || ''],
				rteButtonAddLabel: i18n.messages[value.rteButtonAddLabel || ''],
				rteButtonCancelLabel: i18n.messages[value.rteButtonCancelLabel || ''],
				rteStyle: RTEStyle,
				isDev: EnvironmentHelper.isDev(),
				getUrl: (imageMedia?: ImageMedia) =>
					MediasUtils.getAuthenticatedUrlFromAuthState({
						url: imageMedia?.url,
						authState: auth,
					}),
				onImageUpload: MediasApi.uploadRteImage,
			}}
		>
			{children}
		</RTEContext.Provider>
	);
};
